/**
 * A server-joined report's attributes, carried into a decision file beside the queue's own columns
 * (bata-platform ADR 9018, bata-ui issue 470 consumption). Pure: no React, no network.
 *
 * The platform's per-app reports (`…/report/replenishment`, `…/report/transfers`,
 * `…/report/pricing`, `…/consolidation/runs/{run_id}/report/lines`) serve each line's labels,
 * stock and sales with an absent reason per field. A file joins one report on the line's id and
 * writes, per attribute, the value and then — in a technical column — why it is empty. A line the
 * report does not carry (a report that could not be read, or a newer line) keeps every attribute
 * absent with `not_in_report`, never a guess.
 *
 * Shared because three chapters join a report into their files (G11: "shared primitives live in
 * one place and are imported, not copied"); each chapter declares its own attributes and keys.
 */

import type { ExportCell, ExportColumn } from '../export';

/** The reason every attribute of a line the report does not carry is absent for. */
export const NOT_IN_REPORT = 'not_in_report';

const ABSENT: ExportCell = { kind: 'missing', state: 'not-reported' };

export type ReportAttributeKind = 'text' | 'integer' | 'number' | 'date';

/** One attribute of a report row, as a file column. */
export interface ReportAttribute<Row> {
  readonly key: string;
  readonly kind: ReportAttributeKind;
  readonly value: (row: Row) => string | number | null;
  /** The report's absent reason for the value; `null` where the value is present. */
  readonly reason: (row: Row) => string | null;
  readonly headerKey: string;
  readonly descriptionKey: string;
  readonly decimals?: number;
}

/** The keys the `…_absent_reason` columns are headed and explained by. */
export interface AbsentReasonKeys {
  /** A header with a `{column}` placeholder, e.g. «Why empty: {column}». */
  readonly headerKey: string;
  readonly descriptionKey: string;
}

function cell(kind: ReportAttributeKind, value: string | number | null): ExportCell {
  if (value === null || value === '') return ABSENT;
  if (kind === 'integer') return { kind: 'integer', raw: Number(value) };
  if (kind === 'number') {
    const raw = Number(value);
    // A decimal string that does not parse is absent, never NaN and never 0.
    return Number.isFinite(raw) ? { kind: 'number', raw } : ABSENT;
  }
  if (kind === 'date') return { kind: 'date', iso: String(value) };
  return { kind: 'text', value: String(value) };
}

/** The columns: each attribute, then each attribute's absent reason (technical). Pure. */
export function reportAttributeColumns<Row>(
  attributes: readonly ReportAttribute<Row>[], absent: AbsentReasonKeys,
): ExportColumn[] {
  return [
    ...attributes.map((attribute): ExportColumn => ({
      key: attribute.key, headerKey: attribute.headerKey, kind: attribute.kind, descriptionKey: attribute.descriptionKey,
      ...(attribute.decimals === undefined ? {} : { decimals: attribute.decimals }),
    })),
    // `…_absent_reason` columns are technical without saying so (`ExportColumn.technical`).
    ...attributes.map((attribute): ExportColumn => ({
      key: `${attribute.key}_absent_reason`,
      headerKey: absent.headerKey,
      headerValues: { column: attribute.key },
      kind: 'text',
      descriptionKey: absent.descriptionKey,
    })),
  ];
}

/** The cells of one line, in the columns' order; `row` undefined is a line not in the report. Pure. */
export function reportAttributeCells<Row>(attributes: readonly ReportAttribute<Row>[], row: Row | undefined): ExportCell[] {
  const values = attributes.map((attribute) => (row === undefined ? ABSENT : cell(attribute.kind, attribute.value(row))));
  const reasons = attributes.map((attribute): ExportCell => {
    const reason = row === undefined ? NOT_IN_REPORT : attribute.value(row) === null ? attribute.reason(row) : null;
    return { kind: 'text', value: reason ?? '' };
  });
  return [...values, ...reasons];
}
